import { Injectable } from "@angular/core";
import { Http, Response } from "@angular/http";
import { Observable } from "rxjs/Observable";
import { CompaniesService } from "../companies/companies.service";
import { ApiService } from "../shared";
import { ProjectsService } from "../projects/projects.service";
import { ConfigService } from "../config.service";
import { PollResponsesService } from "../pollresponses/pollresponses.service";
import * as Joi from "joi";

@Injectable()
export class PollsService {
  static currentPoll: any;
  private serviceUrl = ConfigService.serviceUrl;
  private pollSchema: Joi.Schema = Joi.object().keys({
    active: Joi.bool().required(),
    context: Joi.object().required().keys({
      project_ids: Joi.array().required(),
      employer_ids: Joi.array().required(),
      nationalities: Joi.array().required(),
      countries: Joi.array().required(),
      notificationSent: Joi.boolean()
    }),
    default_lang: Joi.string().min(1).required(),
    content: Joi.object()
      .min(1)
      .required()
      .keys(
        ConfigService.languages.reduce((obj, curr) => {
          obj[curr.key] = Joi.object().keys({
            author: Joi.string(),
            title: Joi.string().min(1).required(),
            question: Joi.string().min(1).required(),
            description: Joi.string().min(1).required(),
            // missing array.unique(function) declaration in node_modules\@types\joi\index.d.ts
            // responses: Joi.array().min(1).required().unique((a, b) => a.value === b.value).items(Joi.object().keys({
            responses: Joi.array()
              .min(1)
              .required()
              .unique()
              .items(
                Joi.object().keys({
                  value: Joi.string().min(1).required(),
                  text: Joi.string().min(1).required()
                })
              )
          });
          return obj;
        }, {})
      ),
    id: Joi.string(),
    created_at: Joi.date(),
    updated_at: Joi.date(),
    author_id: Joi.string(),
    user_id: Joi.string(),
    company_id: Joi.string()
  });

  constructor(
    private http: Http,
    private responsesService: PollResponsesService,
    private companiesService: CompaniesService,
    private apiService: ApiService,
    private projectsService: ProjectsService
  ) {}

  getPolls(): Observable<any[]> {
    return this.http
      .get(`${this.serviceUrl}/polls`)
      .map((res) => {
        return this.extractData(res, this);
      })
      .map((polls) => {
        let pollList = polls;
        pollList.map((it) => {
          this.getResponses(it.id).subscribe((responses) => {
            it.responses = responses;
            it.responseCount = responses.length;
          });
        });
        return pollList;
      })
      .catch(this.handleError);
  }

  createPoll(poll: any): Observable<any> {
    return Observable.create((observer) => {
      Joi.validate(poll, this.pollSchema, (err, v) => {
        if (err) {
          observer.error(err);
          observer.complete();
        } else {
          this.http.post(`${this.serviceUrl}/polls`, poll).subscribe(
            (resp) => {
              observer.next(this.extractData(resp, null));
              observer.complete();
            },
            (error) => {
              observer.error(this.handleError(error));
              observer.complete();
            }
          );
        }
      });
    });
  }

  editPoll(poll: any): Observable<any> {
    return Observable.create((observer) => {
      Joi.validate(poll, this.pollSchema, (err, v) => {
        if (err) {
          observer.error(err);
          observer.complete();
        } else {
          this.http.put(`${this.serviceUrl}/polls/` + poll.id, poll).subscribe(
            (resp) => {
              observer.next(this.extractData(resp, null));
              observer.complete();
            },
            (error) => {
              observer.error(this.handleError(error));
              observer.complete();
            }
          );
        }
      });
    });
  }

  deletePoll(id: string) {
    return this.http.delete(`${this.serviceUrl}/polls/` + id).map(
      (resp) => {},
      (error) => {
        this.handleError(error);
      }
    );
  }

  getProjects(): Observable<any> {
    return this.projectsService.getProjectList();
  }

  getNationalities(): any[] {
    return ConfigService.countryList;
  }

  getEmployers(): Observable<any> {
    return this.companiesService.getEmployers();
  }

  getResponseCount(pollId: any): Observable<number> {
    return this.responsesService
      .getPollResponsesCount(pollId)
      .map((count) => {
        return count;
      })
      .catch(this.handleError);
  }

  getResponses(pollId: string): Observable<any> {
    return this.responsesService
      .getPollResponses(pollId)
      .map((responses) => {
        return responses;
      })
      .catch(this.handleError);
  }

  getLanguages(): any[] {
    return ConfigService.languages;
  }

  getProjectNames(posts: any[], self: any): Observable<any[]> {
    return self.projectsService.getProjectList().subscribe((projectList) => {
      posts.map((post) => {
        post.context.projectNames = [];
        if (post.context.project_ids.length < 1) {
          post.context.projectNames = ["<any>"];
        } else {
          post.context.project_ids.map((id) => {
            let projects = projectList.filter((it) => {
              return it.id === id;
            });
            if (projects.length > 0) {
              post.context.projectNames.push(projects[0].name);
            }
          });
        }
      });
      return posts;
    });
  }

  getCountryNames(ids: string[]): string[] {
    let names = ApiService.getCountryNames(ids);
    if (names.length > 0) {
      return names;
    }
    return ["<any>"];
  }

  getEmployerNames(posts: any[], self: any): Observable<any[]> {
    return self.companiesService.getEmployers().subscribe((employerList) => {
      posts.map((post) => {
        post.context.employerNames = [];
        if (post.context.employer_ids.length < 1) {
          post.context.employerNames = ["<any>"];
        } else {
          post.context.employer_ids.map((id) => {
            let employers = employerList.filter((it) => {
              return it.id === id;
            });
            if (employers.length > 0) {
              post.context.employerNames.push(employers[0].name);
            }
          });
        }
      });
      return posts;
    });
  }

  getItemsPerPageValues(): number[] {
    return ConfigService.itemsPerPageValues;
  }

  getItemsPerPage(): number {
    return ApiService.getItemsPerPage();
  }

  saveItemsPerPage(value: number) {
    ApiService.saveItemsPerPage(value);
  }

  private extractData(res: Response, self: any) {
    let body = res.json();
    if (self) {
      self.getProjectNames(body, self);
      self.getEmployerNames(body, self);
    }
    if (Array.isArray(body)) {
      body.forEach((it) => {
        it.context.countries = it.context.countries || [];
      });
    } else {
      body.context.countries = body.context.countries || [];
    }
    return body;
  }

  private handleError(error: Response | any) {
    let errMsg: string;
    if (error instanceof Response) {
      const body = error.json() || "";
      const err = body.error || JSON.stringify(body);
      errMsg = `${error.status} - ${error.statusText || ""} ${err}`;
    } else {
      errMsg = error.message ? error.message : error.toString();
    }
    console.error(errMsg);
    return Observable.throw(errMsg);
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/polls/polls.service.ts